import React from "react";
import { Switch, Route, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { NutritionToggleProvider } from "@/contexts/NutritionToggleContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import UploadPage from "@/pages/UploadPage";
import DashboardPage from "@/pages/DashboardPage";
import ValidatePage from "@/pages/ValidatePage";
import ExportPage from "@/pages/ExportPage";
import RecipesPage from "@/pages/RecipesPage";
import CookbookHomePage from "@/pages/CookbookHomePage";
import RecipeDetailPage from "@/pages/RecipeDetailPage";
import RecipeEditorPage from "@/pages/RecipeEditorPage";
import NewRecipePage from "@/pages/NewRecipePage";
import ScanRecipePage from "@/pages/ScanRecipePage";
import PasteRecipePage from "@/pages/PasteRecipePage";
import CategoriesPage from "@/pages/CategoriesPage";
import SearchPage from "@/pages/SearchPage";
import FavoritesPage from "@/pages/FavoritesPage";
import CollectionDetailPage from "@/pages/CollectionDetailPage";
import LoginPage from "@/pages/LoginPage";
import ObservabilityPage from "@/pages/ObservabilityPage";
import DuplicatesPage from "@/pages/DuplicatesPage";
import ShoppingListPage from "@/pages/ShoppingListPage";
import NotFound from "@/pages/not-found";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function RouteBoundary({ children }: { children: React.ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { loggedIn } = useAuth();

  if (loggedIn === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "hsl(40 33% 96%)" }}
      >
        <div className="flex flex-col items-center gap-3">
          <img src="/logo.png" alt="" className="w-12 h-12 object-contain opacity-60 animate-pulse" />
        </div>
      </div>
    );
  }

  if (loggedIn === false) {
    return <Redirect to="/login" />;
  }

  return <>{children}</>;
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={LoginPage} />

      <Route path="/">
        <RouteBoundary><AuthGate><CookbookHomePage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/recipe/new">
        <RouteBoundary><AuthGate><NewRecipePage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/recipe/scan">
        <RouteBoundary><AuthGate><ScanRecipePage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/recipe/paste">
        <RouteBoundary><AuthGate><PasteRecipePage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/recipe/:id/edit">
        {(params) => (
          <RouteBoundary key={`edit-${params.id}`}>
            <AuthGate><RecipeEditorPage /></AuthGate>
          </RouteBoundary>
        )}
      </Route>
      <Route path="/recipe/:id">
        {(params) => (
          <RouteBoundary key={`detail-${params.id}`}>
            <AuthGate><RecipeDetailPage /></AuthGate>
          </RouteBoundary>
        )}
      </Route>
      <Route path="/categories">
        <RouteBoundary><AuthGate><CategoriesPage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/search">
        <RouteBoundary><AuthGate><SearchPage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/shopping-list">
        <AuthGate><ShoppingListPage /></AuthGate>
      </Route>
      <Route path="/favorites">
        <AuthGate><FavoritesPage /></AuthGate>
      </Route>
      <Route path="/favorites/collections/:id">
        {(params) => (
          <RouteBoundary key={`coll-${params.id}`}>
            <AuthGate><CollectionDetailPage /></AuthGate>
          </RouteBoundary>
        )}
      </Route>

      <Route path="/admin/upload">
        <RouteBoundary><AuthGate><UploadPage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/admin/dashboard">
        <RouteBoundary><AuthGate><DashboardPage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/admin/validate">
        <RouteBoundary><AuthGate><ValidatePage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/admin/export">
        <RouteBoundary><AuthGate><ExportPage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/admin/recipes">
        <RouteBoundary><AuthGate><RecipesPage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/admin/observability">
        <RouteBoundary><AuthGate><ObservabilityPage /></AuthGate></RouteBoundary>
      </Route>
      <Route path="/admin/duplicates">
        <AuthGate><DuplicatesPage /></AuthGate>
      </Route>
      <Route path="/admin">
        <Redirect to="/admin/dashboard" />
      </Route>

      <Route path="/upload">
        <Redirect to="/admin/upload" />
      </Route>
      <Route path="/dashboard">
        <Redirect to="/admin/dashboard" />
      </Route>
      <Route path="/validate">
        <Redirect to="/admin/validate" />
      </Route>
      <Route path="/export">
        <Redirect to="/admin/export" />
      </Route>
      <Route path="/recipes">
        <Redirect to="/admin/recipes" />
      </Route>

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <NutritionToggleProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <AuthProvider>
              <Router />
            </AuthProvider>
          </WouterRouter>
          <Toaster />
        </NutritionToggleProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
